import { useMemo } from 'react'
import { useParams } from '@reach/router'
import useRolePermission from '_/hooks/use-role-permission'
import { userSelector } from '_/modules/authentication/selectors'
import { getStepStatusLogSelector } from '_/modules/budget/selectors'
import {
  currentServiceOrderSelector,
  getButtonsActionByNameSelector,
} from '_/modules/service-orders/selectors'
import { BUTTONS_ACTION, STEP_STATUS } from '_/utils/constants/service-order'
import { useSelector } from 'react-redux'

export function CanDoButtonAction({ nameButton }) {
  const { budgetId } = useParams()
  const serviceOrder = useSelector(currentServiceOrderSelector)

  const user = useSelector(userSelector)
  const buttonAction = useSelector(getButtonsActionByNameSelector(nameButton))
  const stepStatusLogRaw = useSelector(getStepStatusLogSelector)
  const stepStatusLog = useMemo(
    () => (Array.isArray(stepStatusLogRaw) ? stepStatusLogRaw : []),
    [stepStatusLogRaw]
  )
  const { isTradesman, isSAAS } = useRolePermission()

  const serviceProviderId = useMemo(() => {
    const candidate = user?.get?.('getServiceProviderId') ?? user?.getServiceProviderId
    if (typeof candidate === 'function') return candidate.call(user)
    return candidate
  }, [user])

  const serviceOrderStepStatusName = useMemo(() => {
    const stepStatus = serviceOrder?.stepStatus
    if (!stepStatus) return undefined
    if (typeof stepStatus === 'string') return stepStatus
    if (typeof stepStatus?.get === 'function') return stepStatus.get('name')
    return stepStatus?.name
  }, [serviceOrder?.stepStatus])

  const firstStepStatusLog = useMemo(() => stepStatusLog[0], [stepStatusLog])

  const lastStepStatusLogWithBudget = useMemo(
    () => stepStatusLog.find(log => budgetId === log.budgetId?.toString()),
    [serviceOrder?.activeBudget, stepStatusLog, budgetId]
  )

  const allowedStepStatus = useMemo(
    () => buttonAction?.optionsStepServiceOrder,
    [buttonAction?.optionsStepServiceOrder]
  )

  const currentStepStatusName = useMemo(() => {
    if (budgetId) {
      return lastStepStatusLogWithBudget?.stepStatus || firstStepStatusLog?.stepStatus
    }
    return serviceOrderStepStatusName || firstStepStatusLog?.stepStatus
  }, [
    budgetId,
    lastStepStatusLogWithBudget?.stepStatus,
    firstStepStatusLog,
    serviceOrderStepStatusName,
  ])

  const canDoButtonActionNew = useMemo(() => {
    if (isTradesman || isSAAS) {
      const shouldUseServiceOrderStepStatus =
        budgetId &&
        [BUTTONS_ACTION.EDIT_BUDGET, BUTTONS_ACTION.EDIT_BUDGET_BY_PROVIDER].includes(nameButton) &&
        [STEP_STATUS.WAITING_BUDGET, STEP_STATUS.WAITING_BUDGET_SCHEDULE].includes(
          serviceOrderStepStatusName
        )

      const effectiveStepStatusName = shouldUseServiceOrderStepStatus
        ? serviceOrderStepStatusName
        : currentStepStatusName

      if (
        firstStepStatusLog?.tradesmanId === serviceProviderId ||
        serviceOrder?.tradesmanInPoolCandidates ||
        isSAAS
      ) {
        if (budgetId) {
          const isRouteBudgetEqualToSteplogBudget =
            firstStepStatusLog?.budgetId?.toString() === budgetId

          if (isRouteBudgetEqualToSteplogBudget || !firstStepStatusLog?.budgetId) {
            return allowedStepStatus?.some(
              stepStatus => stepStatus.name === effectiveStepStatusName
            )
          }
        }

        return allowedStepStatus?.some(stepStatus => stepStatus.name === effectiveStepStatusName)
      }

      return false
    }

    if (budgetId) {
      return (
        allowedStepStatus?.some(
          stepStatus => stepStatus.name === lastStepStatusLogWithBudget?.stepStatus
        ) || false
      )
    }

    return allowedStepStatus?.some(stepStatus => stepStatus.name === currentStepStatusName) || false
  }, [
    stepStatusLog,
    lastStepStatusLogWithBudget,
    budgetId,
    serviceProviderId,
    serviceOrder,
    currentStepStatusName,
    nameButton,
    isTradesman,
    isSAAS,
    allowedStepStatus,
    serviceOrderStepStatusName,
  ])

  return canDoButtonActionNew
}
